import { Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { ConfigProvider } from './context/ConfigContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ToastProvider } from './context/ToastContext';
import { AppShell } from './layout/AppShell';
import PageLoading from './components/PageLoading';
import { Account } from './pages/Account';
import { Attendance } from './pages/Attendance';
import { Changelog } from './pages/Changelog';
import { ComingSoon } from './pages/ComingSoon';
import { Home } from './pages/Home';
import { Library } from './pages/Library';
import { Documents } from './pages/Documents';
import { Compensation } from './pages/hr/Compensation';
import { Employees } from './pages/hr/Employees';
import { EmployeeForm } from './pages/hr/EmployeeForm';
import { EmployeePay } from './pages/hr/EmployeePay';
import { HrDashboard } from './pages/hr/HrDashboard';
import { Positions } from './pages/hr/Positions';
import { PositionDetail } from './pages/hr/PositionDetail';
import { ForgotPassword } from './pages/ForgotPassword';
import { Help } from './pages/Help';
import { Leave } from './pages/Leave';
import { Login } from './pages/Login';
import { Overtime } from './pages/Overtime';
import { Payroll } from './pages/Payroll';
import { PayrollRunDetail } from './pages/PayrollRunDetail';
import { Payslips } from './pages/Payslips';
import { Performance } from './pages/Performance';
import { ResetPassword } from './pages/ResetPassword';
import { Separation } from './pages/Separation';
import { SsoCallback } from './pages/SsoCallback';
import { Support } from './pages/Support';
import { Training } from './pages/Training';
import { Vacancies } from './pages/Vacancies';
import { VacancyDetail } from './pages/VacancyDetail';

// Memberships
import { MembershipsDashboard } from './pages/MembershipsDashboard';
import { Members } from './pages/Members';
import { MemberForm } from './pages/MemberForm';
import { MemberDetail } from './pages/MemberDetail';
import { Employers } from './pages/Employers';
import { EmployerForm } from './pages/EmployerForm';
import { EmployerDetail } from './pages/EmployerDetail';
import { MemberStatements } from './pages/MemberStatements';
import { MemberContributions } from './pages/MemberContributions';
import { Statements } from './pages/Statements';
import { ContributionSchedules } from './pages/ContributionSchedules';
import { ContributionScheduleDetail } from './pages/ContributionScheduleDetail';
import { Penalties } from './pages/Penalties';
import { PenaltyDetail } from './pages/PenaltyDetail';

// Benefit Payments (Module 4)
import { BenefitClaims } from './pages/BenefitClaims';
import { BenefitClaimDetail } from './pages/BenefitClaimDetail';
import { BenefitApprovalLevels } from './pages/system/BenefitApprovalLevels';
import { BenefitPaymentBatches } from './pages/BenefitPaymentBatches';
import { BenefitPaymentBatchDetail } from './pages/BenefitPaymentBatchDetail';
import { Cheques } from './pages/Cheques';
import { ChequeDetail } from './pages/ChequeDetail';

// General Ledger Interface & Correcting Journals (Module 5)
import { GlAccountMappings } from './pages/system/GlAccountMappings';
import { GlBatches } from './pages/GlBatches';
import { GlBatchDetail } from './pages/GlBatchDetail';
import { JournalEntries } from './pages/JournalEntries';
import { JournalEntryCreate } from './pages/JournalEntryCreate';
import { JournalEntryDetail } from './pages/JournalEntryDetail';
import { BankAccounts } from './pages/banking/BankAccounts';
import { BankAccountStatementLines } from './pages/banking/BankAccountStatementLines';
import { BankDeposits } from './pages/banking/BankDeposits';
import { BankReconciliationReport } from './pages/reports/banking/BankReconciliationReport';
import { Budgets } from './pages/Budgets';
import { BudgetForm } from './pages/BudgetForm';
import { BudgetDetail } from './pages/BudgetDetail';
import { CommittedBudgetReport } from './pages/reports/budgets/CommittedBudgetReport';
import { BudgetVarianceReport } from './pages/reports/budgets/BudgetVarianceReport';

// Announcements / Events
import { Announcements } from './pages/Announcements';
import { AnnouncementForm } from './pages/AnnouncementForm';
import { AnnouncementDetail } from './pages/AnnouncementDetail';
import { Events } from './pages/Events';
import { EventForm } from './pages/EventForm';
import { EventDetail } from './pages/EventDetail';

// Revenue
import { FinanceDashboard } from './pages/FinanceDashboard';
import { Customers } from './pages/Customers';
import { CustomerDetail } from './pages/CustomerDetail';
import { CustomerForm } from './pages/CustomerForm';
import { CustomerQuotations } from './pages/CustomerQuotations';
import { CustomerQuotationDetail } from './pages/CustomerQuotationDetail';
import { CustomerQuotationForm } from './pages/CustomerQuotationForm';
import { CustomerInvoices } from './pages/CustomerInvoices';
import { CustomerInvoiceDetail } from './pages/CustomerInvoiceDetail';
import { CustomerInvoiceForm } from './pages/CustomerInvoiceForm';
import { Receipts } from './pages/Receipts';
import { ReceiptDetail } from './pages/ReceiptDetail';
import { ReceiptForm } from './pages/ReceiptForm';

// Procurement
import { Suppliers } from './pages/Suppliers';
import { SupplierDetail } from './pages/SupplierDetail';
import { SupplierForm } from './pages/SupplierForm';
import { Procurement } from './pages/Procurement';
import { ProcurementDetail } from './pages/ProcurementDetail';
import { ProcurementForm } from './pages/ProcurementForm';
import { ProcurementOrders } from './pages/ProcurementOrders';
import { ProcurementOrderDetail } from './pages/ProcurementOrderDetail';
import { ProcurementOrderForm } from './pages/ProcurementOrderForm';
import { ProcurementReceiving } from './pages/ProcurementReceiving';
import { GoodsReceiptDetail } from './pages/GoodsReceiptDetail';
import { ProcurementReceiptForm } from './pages/ProcurementReceiptForm';
import { ProcurementPayments } from './pages/ProcurementPayments';
import { VendorInvoiceDetail } from './pages/VendorInvoiceDetail';
import { ProcurementInvoiceForm } from './pages/ProcurementInvoiceForm';
import { ProcurementRecordPaymentForm } from './pages/ProcurementRecordPaymentForm';

// Resources
import { Assets } from './pages/Assets';
import { AssetDetail } from './pages/AssetDetail';
import { AssetForm } from './pages/AssetForm';
import { Facilities } from './pages/Facilities';
import { FacilityDetail } from './pages/FacilityDetail';
import { FacilityForm } from './pages/FacilityForm';
import { Bookings } from './pages/Bookings';
import { BookingDetail } from './pages/BookingDetail';
import { BookingForm } from './pages/BookingForm';
import { Inventory } from './pages/Inventory';
import { InventoryDetail } from './pages/InventoryDetail';
import { InventoryForm } from './pages/InventoryForm';

// Investment Management
import { InvestmentAssets } from './pages/InvestmentAssets';
import { InvestmentPolicy } from './pages/InvestmentPolicy';
import { InvestmentCounterparties } from './pages/InvestmentCounterparties';
import { InvestmentsDashboard } from './pages/InvestmentsDashboard';
import { InvestmentDetail } from './pages/InvestmentDetail';
import { InvestmentForm } from './pages/InvestmentForm';

// Reports
import { ReportsIndex } from './pages/reports/ReportsIndex';
import { ReceiptsReport } from './pages/ReceiptsReport';
import { MemberDirectoryReport } from './pages/reports/memberships/MemberDirectoryReport';
import { NewMembersReport } from './pages/reports/memberships/NewMembersReport';
import { MemberDemographicsReport } from './pages/reports/memberships/MemberDemographicsReport';
import { EmployerRegisterReport } from './pages/reports/memberships/EmployerRegisterReport';
import { ContributionSchedulesReport } from './pages/reports/memberships/ContributionSchedulesReport';
import { AgeingScheduleReport } from './pages/reports/penalties/AgeingScheduleReport';
import { ClaimsByStageReport } from './pages/reports/benefits/ClaimsByStageReport';
import { ClaimsRegisterReport } from './pages/reports/benefits/ClaimsRegisterReport';
import { PaymentBatchesReport } from './pages/reports/benefits/PaymentBatchesReport';
import { ChequeRegisterReport } from './pages/reports/benefits/ChequeRegisterReport';
import { InterfaceLogReport } from './pages/reports/gl/InterfaceLogReport';
import { ExceptionsReport } from './pages/reports/gl/ExceptionsReport';
import { ReconciliationReport } from './pages/reports/gl/ReconciliationReport';
import { AccountLedgerReport } from './pages/reports/gl/AccountLedgerReport';
import { AccountMappingReport } from './pages/reports/gl/AccountMappingReport';
import { CorrectingJournalsReport } from './pages/reports/gl/CorrectingJournalsReport';
import { ReportPacks } from './pages/reports/ReportPacks';
import { TrialBalanceReport } from './pages/reports/consolidated/TrialBalanceReport';
import { IncomeStatementReport } from './pages/reports/consolidated/IncomeStatementReport';
import { BalanceSheetReport } from './pages/reports/consolidated/BalanceSheetReport';
import { StatementOfChangesReport } from './pages/reports/consolidated/StatementOfChangesReport';
import { CashFlowStatement } from './pages/reports/consolidated/CashFlowStatement';
import { ComprehensiveIncomeReport } from './pages/reports/consolidated/ComprehensiveIncomeReport';
import { RatioAnalysisReport } from './pages/reports/consolidated/RatioAnalysisReport';
import { QuotationRegisterReport } from './pages/reports/revenue/QuotationRegisterReport';
import { InvoiceRegisterReport } from './pages/reports/revenue/InvoiceRegisterReport';
import { ARAgeingReport } from './pages/reports/revenue/ARAgeingReport';
import { ReceiptsSummaryReport } from './pages/reports/revenue/ReceiptsSummaryReport';
import { RevenueByCategoryReport } from './pages/reports/revenue/RevenueByCategoryReport';
import { CustomerRegisterReport } from './pages/reports/revenue/CustomerRegisterReport';
import { RequestPipelineReport } from './pages/reports/procurement/RequestPipelineReport';
import { OrderRegisterReport } from './pages/reports/procurement/OrderRegisterReport';
import { ReceivingLogReport } from './pages/reports/procurement/ReceivingLogReport';
import { VendorPaymentsReport } from './pages/reports/procurement/VendorPaymentsReport';
import { APAgeingReport } from './pages/reports/procurement/APAgeingReport';
import { SupplierRegisterReport } from './pages/reports/procurement/SupplierRegisterReport';
import { SpendAnalysisReport } from './pages/reports/procurement/SpendAnalysisReport';
import { VendorPerformanceReport } from './pages/reports/procurement/VendorPerformanceReport';
import { AssetRegisterReport } from './pages/reports/resources/AssetRegisterReport';
import { DepreciationScheduleReport } from './pages/reports/resources/DepreciationScheduleReport';
import { AssetMovementLogReport } from './pages/reports/resources/AssetMovementLogReport';
import { FacilityDirectoryReport } from './pages/reports/resources/FacilityDirectoryReport';
import { StockLevelsReport } from './pages/reports/resources/StockLevelsReport';
import { MovementLogReport } from './pages/reports/resources/MovementLogReport';
import { BookingsRegisterReport } from './pages/reports/resources/BookingsRegisterReport';
import { AttendanceRegisterReport } from './pages/reports/hr/AttendanceRegisterReport';
import { LeaveRegisterReport } from './pages/reports/hr/LeaveRegisterReport';
import { LeaveBalancesReport } from './pages/reports/hr/LeaveBalancesReport';
import { PayrollRegisterReport } from './pages/reports/hr/PayrollRegisterReport';
import { OvertimeReportPage } from './pages/reports/hr/OvertimeReportPage';
import { RecruitmentPipelineReport } from './pages/reports/hr/RecruitmentPipelineReport';
import { PerformanceRegisterReport } from './pages/reports/hr/PerformanceRegisterReport';
import { TrainingRegisterReport } from './pages/reports/hr/TrainingRegisterReport';
import { DocumentsRegisterReport } from './pages/reports/hr/DocumentsRegisterReport';
import { SeparationRegisterReport } from './pages/reports/hr/SeparationRegisterReport';
import { EmployeeRegisterReport } from './pages/reports/hr/EmployeeRegisterReport';
import { PositionsRegisterReport } from './pages/reports/hr/PositionsRegisterReport';
import { TurnoverReport } from './pages/reports/hr/TurnoverReport';
import { ContractExpiryReport } from './pages/reports/hr/ContractExpiryReport';
import { InvestmentRegisterReport } from './pages/reports/investments/InvestmentRegisterReport';
import { AssetAllocationReport } from './pages/reports/investments/AssetAllocationReport';
import { IncomeVsExpectedReport } from './pages/reports/investments/IncomeVsExpectedReport';
import { ChangeLogReport } from './pages/reports/investments/ChangeLogReport';
import { LoanPortfolioReport } from './pages/reports/investments/LoanPortfolioReport';
import { FixedInterestMaturityReport } from './pages/reports/investments/FixedInterestMaturityReport';
import { PropertyLeaseRegisterReport } from './pages/reports/investments/PropertyLeaseRegisterReport';
import { CashLiquidityReport } from './pages/reports/investments/CashLiquidityReport';
import { EquityConcentrationReport } from './pages/reports/investments/EquityConcentrationReport';
import { OffshoreExposureReport } from './pages/reports/investments/OffshoreExposureReport';
import { AnnouncementLogReport } from './pages/reports/announcements/AnnouncementLogReport';
import { ActiveAnnouncementsReport } from './pages/reports/announcements/ActiveAnnouncementsReport';
import { EventCalendarReport } from './pages/reports/events/EventCalendarReport';

// System
import { AccountingPeriods } from './pages/system/AccountingPeriods';
import { Authentication } from './pages/system/Authentication';
import { AuditLog } from './pages/system/AuditLog';
import { ChartOfAccounts } from './pages/system/ChartOfAccounts';
import { ExchangeRates } from './pages/system/ExchangeRates';
import { Lists } from './pages/system/Lists';
import { MemberChecklistItems } from './pages/system/MemberChecklistItems';
import { SeparationChecklistItems } from './pages/system/SeparationChecklistItems';
import { Settings } from './pages/system/Settings';
import { PayGrades } from './pages/system/PayGrades';
import { PayGradeDetail } from './pages/system/PayGradeDetail';
import { OvertimeRateTypes } from './pages/system/OvertimeRateTypes';
import { PayComponents } from './pages/system/PayComponents';
import { LeaveCarryOverRules } from './pages/system/LeaveCarryOverRules';
import { LeaveEntitlementPolicies } from './pages/system/LeaveEntitlementPolicies';
import { PerformanceKpis } from './pages/system/PerformanceKpis';
import { PerformanceKras } from './pages/system/PerformanceKras';
import { Competencies } from './pages/system/Competencies';
import { PerformanceReviewPolicies } from './pages/system/PerformanceReviewPolicies';
import { BenefitTypes } from './pages/system/BenefitTypes';
import { BenefitProviders } from './pages/system/BenefitProviders';
import { BenefitPlans } from './pages/system/BenefitPlans';
import { BenefitPlanDetail } from './pages/system/BenefitPlanDetail';
import { EmployeeBenefitEnrollmentDetail } from './pages/hr/EmployeeBenefitEnrollmentDetail';
import { WorkShifts } from './pages/system/WorkShifts';
import { Jobs } from './pages/system/Jobs';
import { OrgUnits } from './pages/system/OrgUnits';
import { Templates } from './pages/system/Templates';
import { UserDetail } from './pages/system/UserDetail';
import { UserForm } from './pages/system/UserForm';
import { Users } from './pages/system/Users';

// Pass C: the final route set. Adds Memberships, Announcements, Events, and
// every Revenue/Procurement/Resources page Pass B built (those pages
// existed on disk but had no route pointing at them until now — see this
// pass's report for the exact path list, chosen to match what Pass B's own
// pages already assumed via their internal navigate()/<Link> calls).
// Removes the /finance and /investments/membership ComingSoon placeholders
// Pass A left as stand-ins.
function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/sso-callback" element={<SsoCallback />} />

                <Route element={<RequireAuth />}>
                  <Route
                    element={
                      <ConfigProvider>
                        <AppShell />
                      </ConfigProvider>
                    }
                  >
                    <Route path="/" element={<Home />} />
                    <Route path="/library" element={<Library />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/support" element={<Support />} />

                    {/* Not part of a permission-gated module — open to any
                        authenticated user, matching routes/api.php. */}
                    <Route path="/announcements" element={<Announcements />} />
                    <Route path="/announcements/new" element={<AnnouncementForm />} />
                    <Route path="/announcements/:id/edit" element={<AnnouncementForm />} />
                    <Route path="/announcements/:id" element={<AnnouncementDetail />} />

                    <Route path="/events" element={<Events />} />
                    <Route path="/events/new" element={<EventForm />} />
                    <Route path="/events/:id/edit" element={<EventForm />} />
                    <Route path="/events/:id" element={<EventDetail />} />

                    <Route path="/reports" element={<ReportsIndex />} />
                    {/* Announcements/Events reports carry the same "no
                        permission required" shape as their modules. */}
                    <Route path="/reports/announcements/log" element={<AnnouncementLogReport />} />
                    <Route path="/reports/announcements/active" element={<ActiveAnnouncementsReport />} />
                    <Route path="/reports/events/calendar" element={<EventCalendarReport />} />

                    <Route path="/hr" element={<ComingSoon title="Human Resources" />} />
                    {/* Self-service: any authenticated user can reach their
                        own Attendance/Timesheet/Payslips/Overtime/
                        Performance/Training/Documents/Separation, no
                        hr.view required. */}
                    <Route path="/hr/attendance" element={<Attendance />} />
                    <Route path="/hr/payslips" element={<Payslips />} />
                    <Route path="/hr/overtime" element={<Overtime />} />
                    <Route path="/hr/performance" element={<Performance />} />
                    <Route path="/hr/training" element={<Training />} />
                    <Route path="/hr/documents" element={<Documents />} />
                    <Route path="/hr/separation" element={<Separation />} />
                    {/* employee.profile.view so a plain employee can view
                        their own record (self-scoped server-side, and
                        read-only in EmployeeForm.tsx — hr.create/hr.update/
                        hr.archive aren't widened, so it's the same "View"
                        link but every mutating control stays hidden). */}
                    <Route element={<RequireAuth permission="hr.view|employee.profile.view" />}>
                      <Route path="/hr/employees" element={<Employees />} />
                      <Route path="/hr/employees/:id/edit" element={<EmployeeForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="hr.view" />}>
                      <Route path="/hr/dashboard" element={<HrDashboard />} />
                      <Route path="/hr/employees/new" element={<EmployeeForm />} />
                      <Route path="/hr/employees/:id/pay" element={<EmployeePay />} />
                      <Route path="/hr/compensation" element={<Compensation />} />
                      <Route path="/hr/positions" element={<Positions />} />
                      <Route path="/hr/positions/:id" element={<PositionDetail />} />
                      <Route path="/hr/benefit-enrollments/:id" element={<EmployeeBenefitEnrollmentDetail />} />
                      <Route path="/hr/leave" element={<Leave />} />
                      <Route path="/hr/payroll" element={<Payroll />} />
                      <Route path="/hr/payroll/:id" element={<PayrollRunDetail />} />
                      <Route path="/hr/recruitment" element={<Vacancies />} />
                      <Route path="/hr/recruitment/:id" element={<VacancyDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="investments.view" />}>
                      <Route path="/investments/dashboard" element={<InvestmentsDashboard />} />
                      <Route path="/investments" element={<InvestmentAssets />} />
                      <Route path="/investments/policy" element={<InvestmentPolicy />} />
                      <Route path="/investments/counterparties" element={<InvestmentCounterparties />} />
                      <Route path="/investments/:id" element={<InvestmentDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="investments.create" />}>
                      <Route path="/investments/new" element={<InvestmentForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="investments.update" />}>
                      <Route path="/investments/:id/edit" element={<InvestmentForm />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Memberships                                       */}
                    {/* ------------------------------------------------- */}
                    {/* Staff-only (memberships.view), unlike the block
                        below — company-wide contribution/benefit/penalty
                        KPIs aren't for a member-portal user (member.profile.view
                        alone), matching the /analytics/memberships/stats
                        route's own gate in api.php. */}
                    <Route element={<RequireAuth permission="memberships.view" />}>
                      <Route path="/memberships/dashboard" element={<MembershipsDashboard />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.view|member.profile.view" />}>
                      <Route path="/memberships" element={<Members />} />
                      <Route path="/memberships/employers" element={<Employers />} />
                      <Route path="/memberships/employers/:id" element={<EmployerDetail />} />
                      <Route path="/memberships/statements" element={<Statements />} />
                      <Route path="/memberships/:id" element={<MemberDetail />} />
                      <Route path="/memberships/:id/statements" element={<MemberStatements />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.create" />}>
                      <Route path="/memberships/new" element={<MemberForm />} />
                      <Route path="/memberships/employers/new" element={<EmployerForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.update" />}>
                      <Route path="/memberships/:id/edit" element={<MemberForm />} />
                      <Route path="/memberships/employers/:id/edit" element={<EmployerForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.contributions.manage" />}>
                      <Route path="/memberships/:id/contributions" element={<MemberContributions />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.contributions.manage|employer.contributions.view" />}>
                      <Route path="/memberships/contribution-schedules" element={<ContributionSchedules />} />
                      <Route path="/memberships/contribution-schedules/:id" element={<ContributionScheduleDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.reports.view" />}>
                      <Route path="/reports/memberships/member-directory" element={<MemberDirectoryReport />} />
                      <Route path="/reports/memberships/new-members" element={<NewMembersReport />} />
                      <Route
                        path="/reports/memberships/member-demographics"
                        element={<MemberDemographicsReport />}
                      />
                      <Route path="/reports/memberships/employer-register" element={<EmployerRegisterReport />} />
                      <Route
                        path="/reports/memberships/contribution-schedules"
                        element={<ContributionSchedulesReport />}
                      />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Penalties & Surcharges (Module 3)                 */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="penalties.view|employer.penalties.view" />}>
                      <Route path="/memberships/penalties" element={<Penalties />} />
                      <Route path="/memberships/penalties/:id" element={<PenaltyDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="penalties.reports.view" />}>
                      <Route path="/reports/penalties/ageing-schedule" element={<AgeingScheduleReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Employer/Member Benefit Payments                  */}
                    {/* (a Memberships sub-feature, not its own module —  */}
                    {/* see RoleCatalog::MEMBERSHIP_BENEFITS_PERMISSIONS) */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="memberships.benefits.view|member.profile.view" />}>
                      <Route path="/memberships/benefits/claims" element={<BenefitClaims />} />
                      <Route path="/memberships/benefits/claims/:id" element={<BenefitClaimDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.benefits.configure" />}>
                      <Route path="/memberships/benefits/approval-levels" element={<BenefitApprovalLevels />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.benefits.disburse" />}>
                      <Route path="/memberships/benefits/payment-batches" element={<BenefitPaymentBatches />} />
                      <Route path="/memberships/benefits/payment-batches/:id" element={<BenefitPaymentBatchDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.benefits.disburse|memberships.benefits.cheque.sign" />}>
                      <Route path="/memberships/benefits/cheques" element={<Cheques />} />
                      <Route path="/memberships/benefits/cheques/:id" element={<ChequeDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="memberships.benefits.reports.view" />}>
                      <Route path="/reports/benefits/claims-by-stage" element={<ClaimsByStageReport />} />
                      <Route path="/reports/benefits/claims-register" element={<ClaimsRegisterReport />} />
                      <Route path="/reports/benefits/payment-batches" element={<PaymentBatchesReport />} />
                      <Route path="/reports/benefits/cheque-register" element={<ChequeRegisterReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Finance & Accounting                              */}
                    {/* ------------------------------------------------- */}
                    {/* Same OR-list as the /analytics/finance/stats route's
                        own gate in api.php and the AppShell's financeItems
                        visibility check — anyone who can see at least one
                        Finance & Accounting sub-module. */}
                    <Route
                      element={
                        <RequireAuth permission="revenue.view|procurement.view|resources.view|gl_interface.view|banking.view|budgets.view" />
                      }
                    >
                      <Route path="/finance/dashboard" element={<FinanceDashboard />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* General Ledger Interface & Correcting Journals    */}
                    {/* (Module 5)                                        */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="gl_interface.configure" />}>
                      <Route path="/gl/mappings" element={<GlAccountMappings />} />
                    </Route>
                    <Route element={<RequireAuth permission="gl_interface.view" />}>
                      <Route path="/gl/batches" element={<GlBatches />} />
                      <Route path="/gl/batches/:id" element={<GlBatchDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="journals.view" />}>
                      <Route path="/gl/journal-entries" element={<JournalEntries />} />
                      <Route path="/gl/journal-entries/:id" element={<JournalEntryDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="journals.create" />}>
                      <Route path="/gl/journal-entries/new" element={<JournalEntryCreate />} />
                      <Route path="/gl/journal-entries/:id/edit" element={<JournalEntryCreate />} />
                    </Route>
                    <Route element={<RequireAuth permission="gl_interface.reports.view" />}>
                      <Route path="/reports/gl/interface-log" element={<InterfaceLogReport />} />
                      <Route path="/reports/gl/exceptions" element={<ExceptionsReport />} />
                      <Route path="/reports/gl/reconciliation" element={<ReconciliationReport />} />
                      <Route path="/reports/gl/account-mapping" element={<AccountMappingReport />} />
                      <Route path="/reports/gl/account-ledger" element={<AccountLedgerReport />} />
                    </Route>
                    <Route element={<RequireAuth permission="journals.reports.view" />}>
                      <Route path="/reports/gl/correcting-journals" element={<CorrectingJournalsReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Banking                                           */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="banking.view" />}>
                      <Route path="/banking/accounts" element={<BankAccounts />} />
                      <Route path="/banking/accounts/:id/statement-lines" element={<BankAccountStatementLines />} />
                      <Route path="/banking/deposits" element={<BankDeposits />} />
                    </Route>
                    <Route element={<RequireAuth permission="banking.reports.view" />}>
                      <Route path="/reports/banking/reconciliation" element={<BankReconciliationReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Budgets                                           */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="budgets.view" />}>
                      <Route path="/budgets" element={<Budgets />} />
                      <Route path="/budgets/:id" element={<BudgetDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="budgets.create" />}>
                      <Route path="/budgets/new" element={<BudgetForm />} />
                      <Route path="/budgets/:id/edit" element={<BudgetForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="budgets.reports.view" />}>
                      <Route path="/reports/budgets/committed" element={<CommittedBudgetReport />} />
                      <Route path="/reports/budgets/variance" element={<BudgetVarianceReport />} />
                    </Route>

                    <Route element={<RequireAuth permission="consolidated.reports.view" />}>
                      <Route path="/reports/consolidated/trial-balance" element={<TrialBalanceReport />} />
                      <Route path="/reports/consolidated/income-statement" element={<IncomeStatementReport />} />
                      <Route path="/reports/consolidated/balance-sheet" element={<BalanceSheetReport />} />
                      <Route path="/reports/consolidated/statement-of-changes" element={<StatementOfChangesReport />} />
                      <Route path="/reports/consolidated/cash-flow" element={<CashFlowStatement />} />
                      <Route path="/reports/consolidated/comprehensive-income" element={<ComprehensiveIncomeReport />} />
                      <Route path="/reports/consolidated/ratio-analysis" element={<RatioAnalysisReport />} />
                    </Route>
                    <Route
                      element={
                        <RequireAuth permission="memberships.reports.view|revenue.reports.view|procurement.reports.view|resources.reports.view|hr.reports.view|investments.reports.view|penalties.reports.view|gl_interface.reports.view|memberships.benefits.reports.view|journals.reports.view|consolidated.reports.view|supplier.reports.view" />
                      }
                    >
                      <Route path="/reports/custom" element={<ReportPacks />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Revenue                                           */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="revenue.customers.view|customer.profile.view" />}>
                      <Route path="/revenue/customers" element={<Customers />} />
                      <Route path="/revenue/customers/:id" element={<CustomerDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="revenue.customers.manage" />}>
                      <Route path="/revenue/customers/new" element={<CustomerForm />} />
                      <Route path="/revenue/customers/:id/edit" element={<CustomerForm />} />
                    </Route>

                    <Route element={<RequireAuth permission="revenue.view|customer.profile.view" />}>
                      <Route path="/revenue/quotations" element={<CustomerQuotations />} />
                      <Route path="/revenue/quotations/:id" element={<CustomerQuotationDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="revenue.view|customer.profile.view" />}>
                      <Route path="/revenue/invoices" element={<CustomerInvoices />} />
                      <Route path="/revenue/invoices/:id" element={<CustomerInvoiceDetail />} />
                      <Route path="/revenue/receipts" element={<Receipts />} />
                      <Route path="/revenue/receipts/:id" element={<ReceiptDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="revenue.create" />}>
                      <Route path="/revenue/quotations/new" element={<CustomerQuotationForm />} />
                      <Route path="/revenue/invoices/new" element={<CustomerInvoiceForm />} />
                      <Route path="/revenue/receipts/new" element={<ReceiptForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="revenue.reports.view" />}>
                      <Route path="/reports/revenue/quotation-register" element={<QuotationRegisterReport />} />
                      <Route path="/reports/revenue/invoice-register" element={<InvoiceRegisterReport />} />
                      <Route path="/reports/revenue/ar-ageing" element={<ARAgeingReport />} />
                      <Route path="/reports/revenue/receipts-summary" element={<ReceiptsSummaryReport />} />
                      <Route path="/reports/revenue/by-category" element={<RevenueByCategoryReport />} />
                      <Route path="/reports/revenue/customer-register" element={<CustomerRegisterReport />} />
                    </Route>
                    <Route element={<RequireAuth permission="revenue.view|customer.profile.view" />}>
                      <Route path="/reports/receipts" element={<ReceiptsReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Procurement                                       */}
                    {/* ------------------------------------------------- */}
                    <Route
                      element={
                        <RequireAuth permission="procurement.view|procurement.suppliers.view|supplier.orders.view" />
                      }
                    >
                      <Route path="/procurement/suppliers" element={<Suppliers />} />
                      <Route path="/procurement/suppliers/:id" element={<SupplierDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="procurement.suppliers.manage" />}>
                      <Route path="/procurement/suppliers/new" element={<SupplierForm />} />
                      <Route path="/procurement/suppliers/:id/edit" element={<SupplierForm />} />
                    </Route>

                    <Route element={<RequireAuth permission="procurement.view" />}>
                      <Route path="/procurement/requests" element={<Procurement />} />
                      <Route path="/procurement/requests/:id" element={<ProcurementDetail />} />
                    </Route>
                    <Route
                      element={
                        <RequireAuth permission="procurement.view|supplier.orders.view|supplier.documents.view" />
                      }
                    >
                      <Route path="/procurement/orders" element={<ProcurementOrders />} />
                      <Route path="/procurement/orders/:id" element={<ProcurementOrderDetail />} />
                      <Route path="/procurement/receiving" element={<ProcurementReceiving />} />
                      <Route path="/procurement/receiving/:id" element={<GoodsReceiptDetail />} />
                      <Route path="/procurement/payments" element={<ProcurementPayments />} />
                      <Route path="/procurement/payments/:id" element={<VendorInvoiceDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="procurement.create" />}>
                      <Route path="/procurement/requests/new" element={<ProcurementForm />} />
                      <Route path="/procurement/orders/new" element={<ProcurementOrderForm />} />
                      <Route path="/procurement/receiving/new" element={<ProcurementReceiptForm />} />
                      <Route path="/procurement/payments/new" element={<ProcurementInvoiceForm />} />
                      <Route
                        path="/procurement/payments/voucher/:voucherId/pay"
                        element={<ProcurementRecordPaymentForm />}
                      />
                    </Route>
                    <Route element={<RequireAuth permission="procurement.reports.view|supplier.reports.view" />}>
                      <Route path="/reports/procurement/request-pipeline" element={<RequestPipelineReport />} />
                      <Route path="/reports/procurement/order-register" element={<OrderRegisterReport />} />
                      <Route path="/reports/procurement/receiving-log" element={<ReceivingLogReport />} />
                      <Route path="/reports/procurement/vendor-payments" element={<VendorPaymentsReport />} />
                      <Route path="/reports/procurement/ap-ageing" element={<APAgeingReport />} />
                      <Route path="/reports/procurement/supplier-register" element={<SupplierRegisterReport />} />
                      <Route path="/reports/procurement/spend-analysis" element={<SpendAnalysisReport />} />
                      <Route path="/reports/procurement/vendor-performance" element={<VendorPerformanceReport />} />
                    </Route>

                    {/* ------------------------------------------------- */}
                    {/* Resources                                         */}
                    {/* ------------------------------------------------- */}
                    <Route element={<RequireAuth permission="resources.view" />}>
                      <Route path="/resources/assets" element={<Assets />} />
                      <Route path="/resources/assets/:id" element={<AssetDetail />} />
                      <Route path="/resources/facilities" element={<Facilities />} />
                      <Route path="/resources/facilities/:id" element={<FacilityDetail />} />
                      <Route path="/resources/bookings" element={<Bookings />} />
                      <Route path="/resources/bookings/:id" element={<BookingDetail />} />
                      <Route path="/resources/inventory" element={<Inventory />} />
                      <Route path="/resources/inventory/:id" element={<InventoryDetail />} />
                    </Route>
                    <Route element={<RequireAuth permission="resources.create" />}>
                      <Route path="/resources/assets/new" element={<AssetForm />} />
                      <Route path="/resources/facilities/new" element={<FacilityForm />} />
                      <Route path="/resources/bookings/new" element={<BookingForm />} />
                      <Route path="/resources/inventory/new" element={<InventoryForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="resources.update" />}>
                      <Route path="/resources/assets/:id/edit" element={<AssetForm />} />
                      <Route path="/resources/facilities/:id/edit" element={<FacilityForm />} />
                      <Route path="/resources/bookings/:id/edit" element={<BookingForm />} />
                    </Route>
                    <Route element={<RequireAuth permission="resources.reports.view" />}>
                      <Route path="/reports/resources/asset-register" element={<AssetRegisterReport />} />
                      <Route path="/reports/resources/depreciation-schedule" element={<DepreciationScheduleReport />} />
                      <Route path="/reports/resources/asset-movement-log" element={<AssetMovementLogReport />} />
                      <Route path="/reports/resources/facility-directory" element={<FacilityDirectoryReport />} />
                      <Route path="/reports/resources/stock-levels" element={<StockLevelsReport />} />
                      <Route path="/reports/resources/movement-log" element={<MovementLogReport />} />
                      <Route path="/reports/resources/bookings-register" element={<BookingsRegisterReport />} />
                    </Route>
                    <Route element={<RequireAuth permission="investments.reports.view" />}>
                      <Route path="/reports/investments/register" element={<InvestmentRegisterReport />} />
                      <Route path="/reports/investments/allocation" element={<AssetAllocationReport />} />
                      <Route path="/reports/investments/income-vs-expected" element={<IncomeVsExpectedReport />} />
                      <Route path="/reports/investments/change-log" element={<ChangeLogReport />} />
                      <Route path="/reports/investments/loan-portfolio" element={<LoanPortfolioReport />} />
                      <Route path="/reports/investments/fixed-interest-maturity" element={<FixedInterestMaturityReport />} />
                      <Route path="/reports/investments/property-lease-register" element={<PropertyLeaseRegisterReport />} />
                      <Route path="/reports/investments/cash-liquidity" element={<CashLiquidityReport />} />
                      <Route path="/reports/investments/equity-concentration" element={<EquityConcentrationReport />} />
                      <Route path="/reports/investments/offshore-exposure" element={<OffshoreExposureReport />} />
                    </Route>
                    <Route element={<RequireAuth permission="hr.reports.view" />}>
                      <Route path="/reports/hr/attendance" element={<AttendanceRegisterReport />} />
                      <Route path="/reports/hr/leave" element={<LeaveRegisterReport />} />
                      <Route path="/reports/hr/leave-balances" element={<LeaveBalancesReport />} />
                      <Route path="/reports/hr/payroll" element={<PayrollRegisterReport />} />
                      <Route path="/reports/hr/overtime" element={<OvertimeReportPage />} />
                      <Route path="/reports/hr/recruitment" element={<RecruitmentPipelineReport />} />
                      <Route path="/reports/hr/performance" element={<PerformanceRegisterReport />} />
                      <Route path="/reports/hr/training" element={<TrainingRegisterReport />} />
                      <Route path="/reports/hr/documents" element={<DocumentsRegisterReport />} />
                      <Route path="/reports/hr/separation" element={<SeparationRegisterReport />} />
                      <Route path="/reports/hr/employees" element={<EmployeeRegisterReport />} />
                      <Route path="/reports/hr/positions" element={<PositionsRegisterReport />} />
                      <Route path="/reports/hr/turnover" element={<TurnoverReport />} />
                      <Route path="/reports/hr/contract-expiry" element={<ContractExpiryReport />} />
                    </Route>

                    <Route element={<RequireAuth permission="users.manage" />}>
                      <Route path="/users" element={<Users />} />
                      <Route path="/users/new" element={<UserForm />} />
                      <Route path="/users/:id" element={<UserDetail />} />
                      <Route path="/users/:id/edit" element={<UserForm />} />
                    </Route>

                    <Route element={<RequireAuth permission="settings.manage" />}>
                      <Route path="/system/separation-checklist-items" element={<SeparationChecklistItems />} />
                      <Route path="/system/member-checklist-items" element={<MemberChecklistItems />} />
                      <Route path="/system/settings" element={<Settings />} />
                      <Route path="/system/lists" element={<Lists />} />
                      <Route path="/system/pay-grades" element={<PayGrades />} />
                      <Route path="/system/pay-grades/:id" element={<PayGradeDetail />} />
                      <Route path="/system/pay-components" element={<PayComponents />} />
                      <Route path="/system/overtime-rate-types" element={<OvertimeRateTypes />} />
                      <Route path="/system/leave-carry-over-rules" element={<LeaveCarryOverRules />} />
                      <Route path="/system/leave-entitlement-policies" element={<LeaveEntitlementPolicies />} />
                      <Route path="/system/performance-kpis" element={<PerformanceKpis />} />
                      <Route path="/system/performance-kras" element={<PerformanceKras />} />
                      <Route path="/system/competencies" element={<Competencies />} />
                      <Route path="/system/performance-review-policies" element={<PerformanceReviewPolicies />} />
                      <Route path="/system/benefit-types" element={<BenefitTypes />} />
                      <Route path="/system/benefit-providers" element={<BenefitProviders />} />
                      <Route path="/system/benefit-plans" element={<BenefitPlans />} />
                      <Route path="/system/benefit-plans/:id" element={<BenefitPlanDetail />} />
                      <Route path="/system/work-shifts" element={<WorkShifts />} />
                      <Route path="/system/jobs" element={<Jobs />} />
                      <Route path="/system/org-units" element={<OrgUnits />} />
                      <Route path="/system/authentication" element={<Authentication />} />
                      <Route path="/system/audit-log" element={<AuditLog />} />
                      <Route path="/system/templates" element={<Templates />} />
                      <Route path="/system/chart-of-accounts" element={<ChartOfAccounts />} />
                      <Route path="/system/accounting-periods" element={<AccountingPeriods />} />
                      <Route path="/system/exchange-rates" element={<ExchangeRates />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
